// src/pages/Scoreboard.tsx
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import AppShell from "../components/AppShell";
import EmptyState from "../components/EmptyState";
import GameCodeChip from "../components/GameCodeChip";
import { usePageTitle } from "../components/PageTitle";
import { useLiveRefresh } from "../hooks/useLiveRefresh";
import { applyDirection, useDirection } from "../lib/direction";

type ScoreRow = {
  name: string;
  points?: number;
  score?: number;
  solves?: number;
  last_solve?: string | null;
  pointsCalc?: number;
  solvesCalc?: number;
};

const pointsOf = (r: ScoreRow) => Number(r.points ?? r.score ?? 0);
const solvesOf = (r: ScoreRow) => Number(r.solves ?? 0);

/** Seat order on the podium: silver, gold, bronze: tallest in the middle. */
const SEATS = [1, 0, 2] as const;

export default function Scoreboard() {
  const { t, i18n } = useTranslation();
  const { isRtl } = useDirection();
  const { gameCode = "" } = useParams<{ gameCode: string }>();
  usePageTitle(t("app.scoreboard"));

  const [rows, setRows] = useState<ScoreRow[]>([]);
  const [loading, setLoading] = useState(true);

  /* Scores arrive by socket push; refetch only when the server says so. */
  const { revision, connected } = useLiveRefresh({ gameCode });

  useEffect(() => {
    applyDirection(i18n.language);
  }, [i18n.language]);

  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const p = pointsOf(b) - pointsOf(a);
      if (p !== 0) return p;
      const s = solvesOf(b) - solvesOf(a);
      if (s !== 0) return s;
      return (a.name || "").localeCompare(b.name || "");
    });
    return list;
  }, [rows]);

  // Top 3 for the podium
  const top3 = useMemo(
    () =>
      sorted
        .slice(0, 3)
        .map((r, i) => ({ rank: i + 1, name: r.name, points: pointsOf(r) })),
    [sorted]
  );

  // Chart data – solves for all teams
  const solvesData = useMemo(
    () =>
      [...rows]
        .sort((a, b) => solvesOf(b) - solvesOf(a) || pointsOf(b) - pointsOf(a))
        .map((r) => ({ team: r.name, solves: solvesOf(r) })),
    [rows]
  );

  // Load on mount, then whenever a push marks the scoreboard stale.
  useEffect(() => {
    let cancelled = false;
    const API = import.meta.env.VITE_API_BASE ?? "/api";

    (async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${API}/public/games/${encodeURIComponent(gameCode)}/scoreboard`,
          { headers: { Accept: "application/json" } }
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        const arr = Array.isArray(data) ? data : data.scoreboard ?? [];
        setRows(
          arr.map((r: ScoreRow) => ({
            ...r,
            pointsCalc: pointsOf(r),
            solvesCalc: solvesOf(r),
          }))
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gameCode, revision]);

  const hasTeams = sorted.length > 0;

  const headerActions = (
    <>
      <Link to={`/play/${encodeURIComponent(gameCode)}`}>
        <Button
          label={t("scoreboard.backToPlay")}
          icon="pi pi-arrow-left"
          className="p-button-sm p-button-text"
        />
      </Link>
    </>
  );

  return (
    <AppShell actions={headerActions} tagline={gameCode}>
      <section className="hero rise">
        <div className="hero__top">
          <GameCodeChip code={gameCode} />
          {/* Updates are always live now, so this reports the connection
              rather than a setting: if the socket drops we say so instead of
              leaving a stale board looking current. */}
          <span
            className={
              connected ? "status status--active" : "status status--lobby"
            }
          >
            <span className="status__dot" aria-hidden="true" />
            {connected ? t("ui.live") : t("ui.reconnecting")}
          </span>
        </div>

        <h1 className="hero__title">{t("scoreboard.livescoreboard")}</h1>

        {hasTeams ? (
          <div className="podium">
            {SEATS.map((idx) => {
              const seat = top3[idx];
              const place = idx + 1;
              return (
                <div
                  key={place}
                  className={`podium__seat podium__seat--${place} ${
                    seat ? "" : "podium__seat--empty"
                  }`}
                >
                  <div className="podium__disc">
                    {place === 1 && seat && (
                      <span className="podium__crown" aria-hidden="true">
                        👑
                      </span>
                    )}
                    {place}
                  </div>
                  <span className="podium__name" title={seat?.name}>
                    {seat?.name ?? "-"}
                  </span>
                  <span className="podium__score">
                    {seat ? `${seat.points.toLocaleString()} ${t("scoreboard.pts")}` : "-"}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="hero__sub" style={{ marginTop: "var(--space-4)" }}>
            {t("scoreboard.noteams")}
          </p>
        )}
      </section>

      {hasTeams && (
        <>
          {/* ---------- Solves ---------- */}
          <div className="section-head">
            <h2 className="section-head__title">
              {t("scoreboard.solveSprint")}
            </h2>
            <span className="section-head__note">
              {t("scoreboard.solveText")}
            </span>
          </div>

          <div className="panel">
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={solvesData}
                  margin={{ top: 8, right: 8, left: -18, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="team"
                    interval={0}
                    tickLine={false}
                    axisLine={false}
                    height={48}
                    /* Long team names collide at any real team count, so they
                       tilt instead of overlapping. */
                    angle={-28}
                    textAnchor="end"
                    reversed={isRtl}
                  />
                  <YAxis
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    orientation={isRtl ? "right" : "left"}
                  />
                  <Tooltip
                    cursor={{ fill: "var(--accent-soft)" }}
                    formatter={(value) => [value, t("scoreboard.solves")]}
                  />
                  <Bar
                    dataKey="solves"
                    name={t("scoreboard.solves")}
                    radius={[6, 6, 0, 0]}
                    fill="var(--accent)"
                    maxBarSize={44}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ---------- Points ---------- */}
          <div className="section-head">
            <h2 className="section-head__title">{t("scoreboard.points")}</h2>
            <span className="section-head__note">
              {t("scoreboard.pointspt")}
            </span>
          </div>

          <div className="rank-list">
            {sorted.map((r, i) => (
              <div
                key={`${r.name}-${i}`}
                className={`rank-row ${i < 3 ? "rank-row--top" : ""}`}
              >
                <span className="rank-row__pos">{i + 1}</span>
                <span className="rank-row__name" title={r.name}>
                  {r.name}
                </span>
                <span className="rank-row__score">
                  {pointsOf(r).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ---------- Full table ---------- */}
      <div className="section-head">
        <h2 className="section-head__title">{t("scoreboard.allteams")}</h2>
      </div>

      <div className="panel">
        {!loading && !hasTeams ? (
          <EmptyState
            icon="pi pi-users"
            title={t("scoreboard.noteams")}
            text={t("ui.noTeamsYetText")}
          />
        ) : (
          <DataTable
            value={sorted}
            loading={loading}
            rowHover
            stripedRows
            scrollable
            scrollHeight="460px"
            emptyMessage={t("scoreboard.noteams")}
          >
            <Column
              header="#"
              body={(_, opt) => (
                <span className="mono">{(opt.rowIndex ?? 0) + 1}</span>
              )}
              style={{ width: "4.5rem", textAlign: "center" }}
            />
            <Column field="name" header={t("scoreboard.team")} />
            <Column
              field="pointsCalc"
              header={t("scoreboard.points")}
              sortable
              body={(r: ScoreRow) => (
                <span className="mono">{pointsOf(r).toLocaleString()}</span>
              )}
              style={{ width: "8rem" }}
            />
            <Column
              field="solvesCalc"
              header={t("scoreboard.solves")}
              sortable
              body={(r: ScoreRow) => (
                <span className="mono">{solvesOf(r)}</span>
              )}
              style={{ width: "7rem" }}
            />
            <Column
              field="last_solve"
              header={t("scoreboard.lastsolve")}
              body={(r: ScoreRow) =>
                r.last_solve ? (
                  <span className="force-ltr">
                    {new Date(r.last_solve).toLocaleString()}
                  </span>
                ) : (
                  "-"
                )
              }
              style={{ width: "15rem" }}
            />
          </DataTable>
        )}
      </div>
    </AppShell>
  );
}
